/**
 * Reader for the refresh history behind a published event — one row per refresh
 * job, as Tina's orchestrator serves it.
 *
 * **The rows come off the wire, not off the event.** `GET /refresh-jobs?
 * admin_event_id=<mood event id>` — i.e. `v_refresh_jobs`, the same view
 * `/tina/refresh-review` and the run show page's refresh table read. They are
 * deliberately *not* read from the published event's `metadata`: `sources`
 * upserts by URL and so can never hold one row per refresh, and a second
 * append-only array inside a Firestore document would duplicate data and grow
 * toward the 1 MB ceiling. The refresh history already exists in Tina — every
 * refresh job is a row Tina stores — so this reads it there.
 *
 * Sibling of `lib/event-sources.ts`, and deliberately not folded into it. The
 * two are different contracts: `sources` is one entry per source URL, rewritten
 * in place, and it is read by the `refresh_review` HITL panel as well as by the
 * edit page; a trace is one entry per completed refresh and only the edit page
 * reads it. Keeping this reader in its own module means a change here cannot
 * reach the HITL panel's reader. The one dependency between them runs one way:
 * this file imports `sourceHostLabel`, never the reverse.
 *
 * Untyped in, typed out, for the same reason `readEventSources` is: this is wire
 * data written by a Tina deploy that may be **older** than this one, so a
 * malformed item degrades to "not shown" rather than throwing inside a sidebar.
 *
 * **No sorting here.** `v_refresh_jobs` is ordered `created_at DESC` and the
 * route returns that order, so the page arrives newest first and this reader
 * hands it on untouched. Re-sorting would be a second opinion about recency that
 * nothing asked for.
 */
import { sourceHostLabel } from "@/lib/event-sources";
import type { IRefreshTrace } from "@/types/event";

/** One page of refresh jobs, as the card renders it. */
export interface RefreshTracesPage {
  /** The rows, in the order the API sent them — newest first. */
  traces: IRefreshTrace[];
  /**
   * How many refresh jobs this event has in total — `total` off the response,
   * **not** `traces.length`. The card fetches a capped page, so the two differ
   * on any event refreshed more often than the cap.
   */
  total: number;
}

/**
 * A `GET /refresh-jobs` page as typed rows, dropping anything unrenderable.
 *
 * Returns an empty page for every shape of nothing — a non-object body, a body
 * with no `items` array, an empty list — so a caller can render conditionally
 * off `traces.length` without first checking the response shape.
 */
export function readRefreshTraces(body: unknown): RefreshTracesPage {
  const items = isRecord(body) && Array.isArray(body.items) ? body.items : [];
  const traces = items.flatMap(toTrace);
  return { traces, total: readTotal(body, traces.length) };
}

/**
 * `total` when the response carries a usable one, else how many rows were read.
 *
 * A malformed body is not a reason to render a wrong count: falling back to the
 * rows in hand is the one number the card can stand behind.
 */
function readTotal(body: unknown, fetched: number): number {
  if (!isRecord(body)) return fetched;
  const total = body.total;
  if (typeof total !== "number" || !Number.isFinite(total) || total < 0) {
    return fetched;
  }
  return total;
}

/**
 * One API item as a card row, or `[]` when it is not renderable.
 *
 * A row has to be able to say *what* was refreshed. `url` is nullable on the
 * wire — a carry-site job seeded not-listed has none — so `site` alone is
 * enough, and the row renders its label as plain text instead of a link. An
 * item with neither is not evidence of anything and is dropped.
 *
 * **A missing `id` or `run_id` is not malformed.** Both are optional in this
 * contract and each only costs the row one link, so the row survives without it
 * rather than being thrown away over a missing href.
 */
function toTrace(item: unknown): IRefreshTrace[] {
  if (!isRecord(item)) return [];

  const url = asText(item.url);
  const site = asText(item.site);
  if (!url && !site) return [];

  const id = asText(item.id);
  const runId = asText(item.run_id);
  const mode = asText(item.mode);
  const outcome = asText(item.refresh_outcome);
  // `finished_at` is when the verdict landed, which is the moment "compared"
  // names; a job still in flight has none and falls back to when it was seeded,
  // because a row with no time at all is worse than one seeded a minute ago.
  const comparedAt = asText(item.finished_at) ?? asText(item.created_at);

  return [
    {
      ...(url ? { url } : {}),
      ...(site ? { site } : {}),
      ...(comparedAt ? { comparedAt } : {}),
      ...(mode ? { mode } : {}),
      ...(outcome ? { outcome } : {}),
      // **Absent stays absent, and that is not the same as empty.**
      // `applied_fields` is new on the Tina side, so an orchestrator deployed
      // before it sends no key at all; `[]` means "this refresh changed
      // nothing". Folding the two together would print "no changes" on rows
      // whose outcome chip says `updated` — a contradiction on one row.
      ...(Array.isArray(item.applied_fields)
        ? { updatedFields: asTextList(item.applied_fields) }
        : {}),
      ...(id ? { tinaItemId: id } : {}),
      ...(runId ? { runId } : {}),
    },
  ];
}

/**
 * The mode as a curator reads it: Tina's `dup` is `duplicate` on screen.
 *
 * An unrecognised mode is printed as itself rather than dropped or blanked —
 * the wire is written by a Tina deploy that may be newer than this one, and a
 * row that says `recheck` is more use than a row with a hole in it where the
 * mode goes. `null` only for a mode that is absent, which the caller renders as
 * no chip at all.
 */
export function refreshTraceModeLabel(mode: string | undefined): string | null {
  if (!mode) return null;
  const normalised = mode.trim().toLowerCase();
  if (normalised === "dup" || normalised === "duplicate") return "duplicate";
  if (normalised === "scheduled") return "scheduled";
  return mode;
}

/**
 * What to call the page a trace refreshed: Tina's `site` when it recorded one,
 * else the URL's host, else the URL, else `null` when the row carries neither.
 *
 * Mirrors `sourceLabel` for the sources list, and reuses its `sourceHostLabel`
 * for the same reason — `allevents/sk/bratislava` beats the hostname, and the
 * hostname beats printing a 120-character URL as its own label.
 */
export function refreshTraceLabel(trace: IRefreshTrace): string | null {
  if (trace.site) return trace.site;
  if (!trace.url) return null;
  return sourceHostLabel(trace.url) ?? trace.url;
}

/**
 * `comparedAt` in local time, or the raw string when it will not parse.
 *
 * Deliberately not "formatted or nothing", matching the sources list: an
 * unparseable timestamp is still evidence about when the refresh ran, and
 * "Invalid Date" is the failure the `movies`/`people` column fix called out.
 * The card renders this under the raw UTC value in a `title`, so a curator
 * diffing it against a Tina log still has the value Tina stored.
 */
export function formatComparedAt(comparedAt: string): string {
  const parsed = new Date(comparedAt);
  if (Number.isNaN(parsed.getTime())) return comparedAt;
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function asTextList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const text = asText(entry);
    return text ? [text] : [];
  });
}
